import { useEffect, useState, useRef } from 'react'
import useAssessment from '../../../../customHooks/useAssessment'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from "react-router-dom"
import PageHeading from '../../../shared/PageHeading'
import ProgressBar from '../ProgressBar'
import classes from './DocumentReviewAssessment.module.scss'
import Button from '../../../shared/ComponentLibrary/RButton'
import Icon from '../../../../Icon'
import Timer from '../Timer'
import PulseWheel from '../PulseWheel'
import { toast } from 'react-toastify'
import PracticeAssessmentFeedback from './DocumentReviewPracticeAssessmentFeedback'
import { scrollDocToTop } from '../../../../helpers/methods'
import { datadogLogs } from '@datadog/browser-logs'


const PracticeAssessment = () => {
  const navigate = useNavigate()
  const { startPracticeTest, submitPracticeTest } = useAssessment()
  const [error, setError] = useState('')
  const [assessmentData, setAssessmentData] = useState<IAssessmentData>({})
  const [eligible, setEligible] = useState(false)
  const [documentNumber, setDocumentNumber] = useState(1)
  const [resetTimer, setResetTimer] = useState(false)
  const [pulseWheelTimeUp, setPulseWheelTimeUp] = useState(false)
  const { register, reset,  handleSubmit } = useForm()
  const scrollRef = useRef<any>()
  const location = useLocation();
  const selectPracticeLanguage = location.state?.language || 'en';

  useEffect(() => startPracticeTest({selectPracticeLanguage, setAssessmentData, setError}), [selectPracticeLanguage])
  useEffect(() => console.log(error), [error])
  useEffect(() => console.log(assessmentData), [assessmentData])

  const submitHandler = (data: any) => {
    let successHandler = (submitData: ISubmitResponseData) => {
      setDocumentNumber((current) => current + 1)
      reset()
      datadogLogs.logger.info('Practice Assessment: Document Questions Submitted', {documentNumberInAssessment: documentNumber, details: submitData});
      scrollDocToTop(scrollRef)
      setResetTimer((current) => !current )
    }
    let errorHandler = () => {
      console.log("practice assessment failed to submit")
    }
    if (assessmentData.legalDocument) {
      let submitData: ISubmitResponseData = {
        attributes: {
          legalDocumentId: assessmentData.legalDocument.id,
          response: [],
          language: selectPracticeLanguage
        },
        setAssessmentData: setAssessmentData,
        setError: setError
      }
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          submitData.attributes?.response.push({ questionId: parseInt(key), answer: data[key] === 'true' })
        }
      })
      console.log(submitData)
      
      submitPracticeTest(submitData, successHandler, errorHandler)
      
    }
  }
  
  const readyToShowPages = () => Boolean(assessmentData.legalDocument?.content.length)
  const forceDoNotShowPages = () => Boolean(!readyToShowPages() || pulseWheelTimeUp)

  useEffect(() => {
    if (assessmentData.eligible?.status === false) {
      navigate('/')
      toast.info(assessmentData.eligible?.message)
    }
    else {
      setEligible(true)
      assessmentData.assessment?.documentNumber && setDocumentNumber(assessmentData.assessment.documentNumber)
    }
  }, [assessmentData.eligible])
  
  return (
    <div className={classes.assessment_container}>
      {
        assessmentData?.isCompleted ? 
        <PracticeAssessmentFeedback assessmentData={assessmentData} /> :
        eligible &&
        <div>
          <div className={classes.header}>
            <PageHeading title='Practice Assessment' icon='testResults' />
            { assessmentData.assessment && <Timer seconds={assessmentData.assessment?.secondsRemaining} showIcon design='total'/> }
          </div>
          <ProgressBar currentValue={documentNumber - 1} maxValue={3}/>

          <form onSubmit={handleSubmit(submitHandler)}>
            <div className={`${classes.assessment_section} ${forceDoNotShowPages() ? classes.pulse_wheel_holder : ''}`}>
              <PulseWheel show={!readyToShowPages()} style={{borderColor: "#2CB6D1", borderTopColor: "transparent"}} timer={60} timeUp={pulseWheelTimeUp} setTimeUp={setPulseWheelTimeUp}>
                <div>
                  <p>The connection appears to be unstable. Please try again later.</p>
                  <p>We recommend taking the assessment from a desktop computer with a stable connection.</p>
                </div>
              </PulseWheel>
              <div className={`${classes.title_section} ${forceDoNotShowPages() ? classes.hidden : ''}`}>
                <div className={classes.title}>
                  <div><b>Document {documentNumber}:</b></div>
                  "{ assessmentData.legalDocument && assessmentData.legalDocument.title }"
                </div>
                { assessmentData.assessment && <Timer seconds={assessmentData.assessment?.secondsElapsed} incremental resetTimer={resetTimer} design='section' /> }
              </div>
              <hr className={classes.divider}/>

              <div className={`${classes.questions_section} ${forceDoNotShowPages() ? classes.hidden : ''}`}>
                <div className={classes.document_section} ref={scrollRef}>
                  <div className={classes.document}>
                    {assessmentData.legalDocument?.content && (
                      <div className={classes.document_content}>
                        {assessmentData.legalDocument.content}
                      </div>
                    )}
                  </div>
                </div>
                <div className={classes.questions}>
                  {
                    assessmentData.assessment && assessmentData.assessment.questions.map((question) => {
                      return (
                        <div className={classes.question} key={question.id}>
                          <div className={classes.text}>
                            <b>{question.id}. </b>
                            <div>{question.question}</div>
                          </div>
                          <div className={classes.options}>
                            <div>
                              <label>
                                <input
                                  type="radio"
                                  {...register(question.id.toString())}
                                  value="true"
                                  id={`question_${question.id}_true`}
                                  data-dd-privacy="allow"
                                /> Yes
                              </label>
                            </div>
                            <div>
                              <label>
                                <input
                                  type="radio"
                                  {...register(question.id.toString())}
                                  value="false"
                                  id={`question_${question.id}_false`}
                                  data-dd-privacy="allow"
                                /> No
                              </label>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  }
                </div>
              </div>
            </div>
            <div className={classes.buttons}>
              <div className={classes.cancel_button} />
              <div className={classes.submit_button}>
                <Button type='submit' hidden={forceDoNotShowPages()}>
                  Next Document
                  <Icon name='longArrowLeft' color='#FFF' transform />
                </Button>  
              </div>
            </div>
          </form>
        </div>
      }
    </div>
  )
}

export default PracticeAssessment